import type { ReactNode } from "react";
import { createContext, useContext, useMemo, useRef, useState } from "react";

import type { TextElement } from "@/react/types/Element";
import type { Form } from "@/react/types/Form";

import type { ProfileFieldValue } from "../hooks/useCurrentMemberProfileFieldValues";
import { useCurrentMemberProfileFieldValues } from "../hooks/useCurrentMemberProfileFieldValues";

export type RenderType = "embed" | "standalone";
export type FormUsage = "newsletter" | "page";
export const defaultColors = {
  form: {
    backgroundColor: "#fff",
    color: "#545861",
  },
  button: {
    backgroundColor: "#2B2E33",
    color: "#fff",
  },
  page: {
    backgroundColor: "#F7F9FA",
  },
};

export interface FormRenderingContextValue {
  backendErrors: Record<number, string>;
  buttonBackgroundColor: string;
  buttonTextColor: string;
  form: Form;
  formBackgroundColor: string;
  formDomId: string;
  formHeight: number;
  formStyles: string;
  formTextColor: string;
  index: number;
  initialFocusRef: React.RefObject<HTMLInputElement | null>;
  isLoadingProfileFieldValues: boolean;
  isModal: boolean;
  isPreview: boolean;
  isSubmitting: boolean;
  pageBackgroundColor?: string;
  popupDelay: number;
  popupFrequency: number;
  profileFieldValues: Record<number, ProfileFieldValue>;
  renderType: RenderType;
  setBackendErrors: (value: Record<string, string>) => void;
  setFormHeight: (value: number) => void;
  setIsSubmitting: (value: boolean) => void;
  setShouldRenderEndingPage: (value: boolean) => void;
  shouldRenderEndingPage: boolean;
  titleElement?: TextElement;
  usage?: FormUsage;
}

const FormRenderingContext = createContext<
  FormRenderingContextValue | undefined
>(undefined);

interface FormRenderingProviderProps {
  children: ReactNode;
  form: Form;
  index: number;
  isModal: boolean;
  isPreview: boolean;
  popupDelay?: number;
  popupFrequency?: number;
  renderType: RenderType;
  usage?: FormUsage;
}

export const FormRenderingProvider = ({
  children,
  form,
  index,
  isModal,
  isPreview,
  usage,
  popupDelay,
  popupFrequency,
  renderType,
}: FormRenderingProviderProps) => {
  const [backendErrors, setBackendErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shouldRenderEndingPage, setShouldRenderEndingPage] = useState(false);
  const [formHeight, setFormHeight] = useState<number>(0);
  const initialFocusRef = useRef<HTMLInputElement>(null);
  const titleElement = useMemo(
    () =>
      form.elements.find(
        element =>
          element.type === "text" &&
          element.attributes.text_type === "title" &&
          element.attributes.value,
      ) as TextElement | undefined,
    [form.elements],
  );

  const { profileFieldValues, isLoading: isLoadingProfileFieldValues } =
    useCurrentMemberProfileFieldValues();
  const {
    formBackgroundColor,
    formTextColor,
    buttonBackgroundColor,
    buttonTextColor,
    pageBackgroundColor,
    formDomId,
    formStyles,
  } = useMemo(() => {
    let styles;
    if (renderType === "embed") {
      styles = form.embed_styles;
    } else if (renderType === "standalone") {
      styles = form.standalone_page_styles;
    }

    const formBackgroundColor =
      styles?.form?.backgroundColor || defaultColors.form.backgroundColor;
    const formTextColor = styles?.form?.color || defaultColors.form.color;
    const buttonBackgroundColor =
      styles?.button?.backgroundColor || defaultColors.button.backgroundColor;
    const buttonTextColor = styles?.button?.color || defaultColors.button.color;
    const pageBackgroundColor =
      renderType === "standalone"
        ? form.standalone_page_styles?.page?.backgroundColor ||
          defaultColors.page.backgroundColor
        : undefined;

    const formDomId = `form-${form.uid}`;
    const formStyles = `#${formDomId} label > span,
     #${formDomId} label,
     #${formDomId} select,
     #${formDomId} h3,
     #${formDomId} h4,
     #${formDomId} h5,
     #${formDomId} p:not(.form-group__error) {
       color: ${formTextColor};
     }`;

    return {
      formBackgroundColor,
      formTextColor,
      buttonBackgroundColor,
      buttonTextColor,
      pageBackgroundColor,
      formDomId,
      formStyles,
    };
  }, [renderType, form.embed_styles, form.standalone_page_styles, form.uid]);

  const value = {
    backendErrors,
    buttonBackgroundColor,
    buttonTextColor,
    form,
    formBackgroundColor,
    formDomId,
    formHeight,
    formStyles,
    formTextColor,
    index,
    initialFocusRef,
    isLoadingProfileFieldValues,
    isModal,
    isPreview,
    isSubmitting,
    usage,
    pageBackgroundColor,
    popupDelay: popupDelay ?? form.popup_delay,
    popupFrequency: popupFrequency ?? form.popup_frequency,
    profileFieldValues,
    renderType,
    setBackendErrors,
    setFormHeight,
    setIsSubmitting,
    setShouldRenderEndingPage,
    shouldRenderEndingPage,
    titleElement,
  };

  return (
    <FormRenderingContext.Provider value={value}>
      {children}
    </FormRenderingContext.Provider>
  );
};

export const useFormRenderingContext = () => {
  const context = useContext(FormRenderingContext);
  if (!context) {
    throw new Error(
      "useFormRenderingContext must be used within a FormRenderingProvider",
    );
  }
  return context;
};

FormRenderingContext.displayName = "FormRenderingContext";
